//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  deleteDraftPosts as deleteDraftPostsApi,
  getDraftPosts,
  searchDraftPosts,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApi';
import {
  DraftQuerySortBy,
  IDraftPost,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import DraftId from '@models/DraftId';
import { SocialNetwork } from '@models/SocialNetwork';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { updateMediaItems } from '@redux/schedule-media/scheduleMediaSlice';
import { getCurrentPostingGroupsForRequest } from '@universal-scheduling/redux/calendar/selectors/getUniversalCalendarSettings';
import { setNetworkFilter } from '@universal-scheduling/redux/calendar/universalCalendarSlice';
import {
  getCurrentSearchOffset,
  getDraftSortBy,
  getHasReachedEndOfDraftPosts,
  getIsFetchingAdditionalDraftPosts,
  getIsFetchingDraftPosts,
} from '@universal-scheduling/redux/drafts/selectors/getUniversalDraftsSettings';
import {
  IUniversalDraftsState,
  NormalizedDraftPosts,
  SEARCH_DRAFT_POSTS_REQUEST_LIMIT,
} from '@universal-scheduling/redux/drafts/universalDraftsModel';
import { isDefined } from '@utils/filterPredicates';
import normalize, { Normalized } from '@utils/normalize';
import { pluralize } from '@utils/stringUtils';

const initialState: IUniversalDraftsState = {
  draftPosts: Normalized.empty(),
  totalCount: undefined,

  searchOffset: 0,
  sortBy: DraftQuerySortBy.CreatedAt,

  deleteDraftModal: {
    currentDraftIds: [],
    isOpen: false,
  },

  errors: {
    deleteDraftPostError: undefined,
    fetchAdditionalDraftPostsError: undefined,
    fetchDraftPostsError: undefined,
    fetchDraftPostByIdError: undefined,
  },
  flags: {
    hasReachedEndOfDraftPosts: false,
    isDeletingDraftPost: false,
    isFetchingAdditionalDraftPosts: false,
    isFetchingDraftPosts: false,
    isFetchingDraftPostById: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.UniversalDrafts,
  initialState,
  reducers: {
    requestDraftPostById(state) {
      state.errors.fetchDraftPostByIdError = undefined;
      state.flags.isFetchingDraftPostById = true;
    },
    receiveDraftPostByIdSucceeded(state) {
      state.flags.isFetchingDraftPostById = false;
    },
    receiveDraftPostByIdFailed(
      state,
      { payload: error }: PayloadAction<string>,
    ) {
      state.errors.fetchDraftPostByIdError = error;
      state.flags.isFetchingDraftPostById = false;
    },

    requestDraftPosts(state) {
      state.errors.fetchDraftPostsError = undefined;
      state.flags.hasReachedEndOfDraftPosts = false;
      state.flags.isFetchingDraftPosts = true;
    },
    receiveDraftPostsSucceeded(
      state,
      {
        payload: { normalizedDrafts, totalCount },
      }: PayloadAction<{
        normalizedDrafts: NormalizedDraftPosts;
        totalCount: number;
      }>,
    ) {
      const postCount = normalizedDrafts.orderedIds.length;

      state.flags.isFetchingDraftPosts = false;
      state.flags.hasReachedEndOfDraftPosts =
        postCount < SEARCH_DRAFT_POSTS_REQUEST_LIMIT;
      state.draftPosts = normalizedDrafts;
      state.searchOffset = postCount;
      state.totalCount = totalCount;
    },
    receiveDraftPostsFailed(state, { payload: error }: PayloadAction<string>) {
      state.errors.fetchDraftPostsError = error;
      state.flags.isFetchingDraftPosts = false;
    },

    requestAdditionalDraftPosts(state) {
      state.errors.fetchAdditionalDraftPostsError = undefined;
      state.flags.isFetchingAdditionalDraftPosts = true;
    },
    receiveAdditionalDraftPostsSucceeded(
      state,
      {
        payload: { normalizedDrafts, totalCount },
      }: PayloadAction<{
        normalizedDrafts: NormalizedDraftPosts;
        totalCount: number;
      }>,
    ) {
      const additionalPostCount = normalizedDrafts.orderedIds.length;

      const updatedDraftPosts = Normalized.addItems(
        state.draftPosts,
        normalizedDrafts,
      );

      state.flags.isFetchingAdditionalDraftPosts = false;
      state.flags.hasReachedEndOfDraftPosts =
        additionalPostCount < SEARCH_DRAFT_POSTS_REQUEST_LIMIT;
      state.draftPosts = updatedDraftPosts;
      state.totalCount = totalCount;
      state.searchOffset = updatedDraftPosts.orderedIds.length;
    },
    receiveAdditionalDraftPostsFailed(
      state,
      { payload: error }: PayloadAction<string>,
    ) {
      state.errors.fetchAdditionalDraftPostsError = error;
      state.flags.isFetchingAdditionalDraftPosts = false;
    },

    requestDeleteDraftPost(
      state,
      { payload: draftIds }: PayloadAction<DraftId[]>,
    ) {
      state.errors.deleteDraftPostError = undefined;
      state.flags.isDeletingDraftPost = true;
      if (draftIds.length > 0) {
        state.draftPosts = Normalized.removeItems(state.draftPosts, draftIds);
      }
    },
    receiveDeleteDraftPostSucceeded(state) {
      state.flags.isDeletingDraftPost = false;
    },
    receiveDeleteDraftPostsFailed(
      state,
      {
        payload,
      }: PayloadAction<{ drafts: IDraftPost[]; errorMessage: string }>,
    ) {
      state.flags.isDeletingDraftPost = false;
      state.errors.deleteDraftPostError = payload.errorMessage;
      if (payload.drafts.length > 0) {
        state.draftPosts = Normalized.addItems(
          state.draftPosts,
          Normalized.from(payload.drafts, (draft) => draft.id),
        );
      }
    },

    openDeleteDraftModal(
      state,
      { payload: draftIds }: PayloadAction<DraftId[]>,
    ) {
      state.deleteDraftModal.currentDraftIds = draftIds;
      state.deleteDraftModal.isOpen = true;
    },
    closeDeleteDraftModal(state) {
      state.deleteDraftModal.currentDraftIds = [];
      state.deleteDraftModal.isOpen = false;
    },

    setSort(state, { payload: sortBy }: PayloadAction<DraftQuerySortBy>) {
      state.sortBy = sortBy;
    },

    resetDraftsState: () => initialState,
  },
});

export const UniversalDraftsReducer = reducer;
export const { closeDeleteDraftModal, openDeleteDraftModal, resetDraftsState } =
  actions;

export const fetchDraftById = createThunk(
  (draftId: DraftId) => async (dispatch) => {
    const defaultErrorMessage = `Cannot get draft post for draftId: ${draftId}`;

    dispatch(actions.requestDraftPostById());

    try {
      const [draftPost] = await getDraftPosts([draftId]);

      if (!isDefined(draftPost)) {
        throw new Error(defaultErrorMessage);
      }

      dispatch(updateMediaItems(draftPost.mediaItems));

      dispatch(actions.receiveDraftPostByIdSucceeded());

      return draftPost;
    } catch (_e) {
      dispatch(actions.receiveDraftPostByIdFailed(defaultErrorMessage));
    }
  },
);

export const fetchDraftPosts = createThunk(() => async (dispatch, getState) => {
  const state = getState();
  const isFetchingDraftPosts = getIsFetchingDraftPosts(state);
  const postingGroupsFilter = getCurrentPostingGroupsForRequest(state);
  if (postingGroupsFilter.length === 0) {
    return;
  }
  const sortBy = getDraftSortBy(state);

  // Return early if already fetching drafts
  if (isFetchingDraftPosts) {
    return;
  }

  dispatch(actions.requestDraftPosts());

  try {
    const { data: drafts, totalCount } = await searchDraftPosts({
      groups: postingGroupsFilter,
      offset: 0,
      limit: SEARCH_DRAFT_POSTS_REQUEST_LIMIT,
      sortAscending: sortBy === DraftQuerySortBy.ScheduledTime,
      sortBy,
    });

    const normalizedDrafts = normalize(drafts, (post) => post.id);

    dispatch(
      actions.receiveDraftPostsSucceeded({ normalizedDrafts, totalCount }),
    );
  } catch (_e) {
    dispatch(actions.receiveDraftPostsFailed(`Cannot get draft posts`));
  }
});

export const fetchAdditionalDraftPosts = createThunk(
  () => async (dispatch, getState) => {
    const state = getState();
    const hasReachedEndOfDraftPosts = getHasReachedEndOfDraftPosts(state);
    const isFetchingDraftPosts = getIsFetchingDraftPosts(state);
    const isFetchingAdditionalDraftPosts =
      getIsFetchingAdditionalDraftPosts(state);
    const postGroupsFilter = getCurrentPostingGroupsForRequest(state);
    const searchOffset = getCurrentSearchOffset(state);
    const sortBy = getDraftSortBy(state);

    // Return early if already fetching drafts
    // or if we have reached end of draft posts
    if (
      hasReachedEndOfDraftPosts ||
      isFetchingDraftPosts ||
      isFetchingAdditionalDraftPosts
    ) {
      return;
    }

    dispatch(actions.requestAdditionalDraftPosts());

    try {
      const { data: drafts, totalCount } = await searchDraftPosts({
        groups: postGroupsFilter,
        offset: searchOffset,
        limit: SEARCH_DRAFT_POSTS_REQUEST_LIMIT,
        sortAscending: sortBy === DraftQuerySortBy.ScheduledTime,
        sortBy,
      });

      const normalizedDrafts = normalize(drafts, (post) => post.id);

      dispatch(
        actions.receiveAdditionalDraftPostsSucceeded({
          normalizedDrafts,
          totalCount,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveAdditionalDraftPostsFailed(
          `Cannot get additional draft posts`,
        ),
      );
    }
  },
);

interface DeleteDraftPostOptions {
  shouldShowToast?: boolean;
}

export const deleteDraftPost = (
  draftId: DraftId,
  options: DeleteDraftPostOptions = { shouldShowToast: true },
) => {
  return deleteDraftPosts([draftId], options);
};

export const deleteDraftPosts = createThunk(
  (
    draftIds: DraftId[],
    options: DeleteDraftPostOptions = { shouldShowToast: true },
  ) =>
    async (dispatch, getState): Promise<boolean> => {
      dispatch(actions.requestDeleteDraftPost(draftIds));

      const draftPostsToDelete = Normalized.getOrdered(
        getState().universalSchedule.universalDrafts.draftPosts,
      ).filter((draft) => draftIds.includes(draft.id));

      try {
        await deleteDraftPostsApi(draftIds);

        dispatch(actions.receiveDeleteDraftPostSucceeded());

        if (options.shouldShowToast) {
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: `Deleted ${pluralize(draftIds.length, 'Draft', 'Drafts', false)}`,
              type: ToastType.SUCCESS,
              message: `Your ${pluralize(draftIds.length, 'draft has', 'drafts have', false)} been deleted`,
              options: {
                toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
              },
            }),
          );
        }

        return true;
      } catch (_e) {
        dispatch(
          actions.receiveDeleteDraftPostsFailed({
            drafts: draftPostsToDelete,
            errorMessage: `Failed to delete draft ${pluralize(draftIds.length, 'post', 'posts', false)}: ${draftIds.join(', ')}`,
          }),
        );

        if (options.shouldShowToast) {
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: `Unable to Delete ${pluralize(draftIds.length, 'Draft', 'Drafts', false)}`,
              type: ToastType.ERROR,
              message: `We were unable to this ${pluralize(draftIds.length, 'draft', 'drafts', false)}. Please try again later.`,
              options: {
                toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
              },
            }),
          );
        }

        return false;
      }
    },
);

export const setNetworkFilterAndFetchDrafts = createThunk(
  (newNetworkFilter: SocialNetwork | 'All') => async (dispatch) => {
    dispatch(setNetworkFilter(newNetworkFilter));
    await dispatch(fetchDraftPosts());
  },
);

export const setSortAndFetchDrafts = createThunk(
  (newSort: DraftQuerySortBy) => async (dispatch) => {
    dispatch(actions.setSort(newSort));
    await dispatch(fetchDraftPosts());
  },
);
